//
// Report-builder chart registry — ported and simplified from takso-poc.
//
// Charts with backend endpoints in takso-service:
//   • outcomeFrequency   — outcome measure frequencies (horizontal bar per outcome)
//   • outcomeNetwork     — force-directed co-occurrence network
//   • activityTypeLadder — activity type counts grouped by output bucket
//
// The POC's dashboard charts (statusStack, programsSinglesStack, etc.) are omitted.
//
// Usage: ChartHost imports ChartRegistry and delegates fetch + render.

import { useState } from 'react';

import { t } from '../i18n';
import { displayOutcome } from '../lib/outcomeDisplayLabels';
import { OUTCOME_ICONS } from '../lib/outcomeIcons';
import { chartApiGet } from '../lib/chartApi';
import { colorFor } from '../theme/palette';
import { ActivityTypeLadder } from '../components/charts/ActivityTypeLadder';
import { ParticipantLadder } from '../components/ParticipantLadder';
import { ParticipantsByActivityType } from '../components/ParticipantsByActivityType';
import OutcomeNetwork from '../components/reports/OutcomeNetwork';
import { LocaleService } from '../services';

import './charts.scss';

// ── Internal helpers ──────────────────────────────────────────────────────────

// Non-top bars scale to this % of the row. Top bar uses flex:1.
const MAX_BAR_PCT = 93;

// ── ChartRegistry ─────────────────────────────────────────────────────────────

// ── Investment segment definitions ────────────────────────────────────────────
const INVESTMENT_SEGS = [
  { key: 'grants', label: 'Org grants', color: '#1D3498' },
  { key: 'partnerFunding', label: 'Partner funding', color: '#0C95CD' },
  { key: 'partnerInKind', label: 'In-kind', color: '#FDE047' },
  { key: 'other', label: 'Other expenditure', color: '#9CA3AF' },
  { key: 'volunteerValue', label: 'Volunteer value', color: '#EE7A00' },
];

////////////////////////////////////////////////////////////
// formatters linked to LocaleService

// numerals
const fmtN = (n) => LocaleService.fmtLocaleNumeral(n);

// currency formatter
const fmtCurrency = (n) => LocaleService.fmtLocaleCurrency(n);

// compact currency formatter: $1.23m / $123k / $12,345
const fmtCurrencyCompact = (n) => LocaleService.fmtLocaleCurrencyCompact(n);

/**
 * Segmented investment bar with a custom hover tooltip.
 * Defined as a named component so React hooks (useState) are legal.
 * The tooltip is rendered outside the overflow:hidden bar so it isn't clipped.
 */
function InvestmentChart({ values, total }) {
  const [tip, setTip] = useState(null);
  // tip: { label, value, x } where x is pixels from left edge of the bar container

  const handleEnter = (e, seg) => {
    const segRect = e.currentTarget.getBoundingClientRect();
    const barRect = e.currentTarget.parentElement.getBoundingClientRect();
    const x = segRect.left - barRect.left + segRect.width / 2;
    setTip({ label: seg.label, value: seg.value, x });
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* Custom tooltip — above the bar, not clipped by overflow:hidden */}
      {tip && (
        <div
          style={{
            position: 'absolute',
            bottom: '100%',
            left: tip.x,
            transform: 'translateX(-50%)',
            marginBottom: 6,
            background: '#1f2937',
            color: '#fff',
            padding: '4px 10px',
            borderRadius: 5,
            fontSize: 12,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            zIndex: 20,
            boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
          }}
        >
          <span style={{ color: '#9ca3af', marginRight: 4 }}>{tip.label}:</span>
          <span style={{ fontWeight: 700 }}>
            {fmtCurrencyCompact(tip.value)}
          </span>
        </div>
      )}

      {/* Segmented bar */}
      <div
        style={{
          display: 'flex',
          height: 40,
          borderRadius: 5,
          overflow: 'hidden',
          gap: 1,
        }}
        onMouseLeave={() => setTip(null)}
      >
        {values
          .filter((s) => s.value > 0)
          .map((s, i) => {
            const pct = (s.value / total) * 100;
            const textColor = s.color === '#FDE047' ? '#1e3a5f' : '#fff';
            return (
              <div
                key={i}
                onMouseEnter={(e) => handleEnter(e, s)}
                style={{
                  width: `${pct}%`,
                  background: s.color,
                  minWidth: 3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  cursor: 'default',
                }}
              >
                {pct >= 8 && (
                  <span
                    style={{
                      fontSize: pct >= 14 ? 12 : 11,
                      fontWeight: 700,
                      color: textColor,
                      padding: '0 4px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {fmtCurrencyCompact(s.value)}
                  </span>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
}

export const ChartRegistry = {
  // ── Outcome frequency bar chart ────────────────────────────────────────────
  // Simple horizontal bar per outcome, sorted by count desc.
  // Colours bars by domain using the Takso palette.
  outcomeFrequency: {
    async fetch(flags) {
      const params = { ...(flags.filterParams || {}) };
      if (flags.activityIds?.length > 0) {
        params.activityIds = flags.activityIds.join(',');
      } else if (flags.programIds?.length > 0) {
        params.programIds = flags.programIds.join(',');
      } else if (flags.orgId) {
        params.orgId = flags.orgId;
      }
      return chartApiGet('outcome-measures', params);
    },

    render({ data, onOutcomeBarClick, topN }) {
      const outcomes = Array.isArray(data?.outcomes) ? data.outcomes : [];
      const sorted = outcomes
        .filter((o) => Number(o.usage_count) > 0)
        .sort(
          (a, b) =>
            Number(b.usage_count) - Number(a.usage_count) ||
            displayOutcome(a.title, a.id).localeCompare(
              displayOutcome(b.title, b.id)
            )
        );
      const active = topN ? sorted.slice(0, topN) : sorted;
      const maxCount = Math.max(
        ...active.map((o) => Number(o.usage_count) || 0),
        1
      );

      if (active.length === 0) {
        return (
          <div className="chart-empty">{t('charts.no_outcomes_selected')}</div>
        );
      }

      return (
        <div className="chart-bar-list">
          {active.map((o) => {
            const count = Number(o.usage_count) || 0;
            const pct = (count / maxCount) * MAX_BAR_PCT;
            const isTopBar = count === maxCount;
            const label = displayOutcome(o.title, o.id);
            const bg = colorFor(o.domain, 0);
            const icon = OUTCOME_ICONS[o.id];
            const clickable = !!onOutcomeBarClick;

            return (
              <div
                key={o.id}
                className={`chart-bar-row${
                  clickable ? ' chart-bar-row--clickable' : ''
                }`}
                onClick={clickable ? () => onOutcomeBarClick(o) : undefined}
                title={
                  clickable
                    ? t('charts.view_rating_dist_for', { title: o.title })
                    : undefined
                }
              >
                <div className="chart-bar-row__label">{label}</div>
                <div className="chart-bar-row__icon">
                  {icon ? (
                    <img src={`/icons/outcomes/${icon}.png`} alt={label} />
                  ) : (
                    <div className="chart-bar-row__icon-placeholder" />
                  )}
                </div>
                <div className="chart-bar-row__bar-area">
                  <div
                    className={`chart-bar-row__bar${
                      isTopBar ? ' chart-bar-row__bar--top' : ''
                    }`}
                    style={{
                      background: bg,
                      ...(!isTopBar && { width: `${pct}%` }),
                    }}
                  />
                  <span className="chart-bar-row__count">{count}</span>
                </div>
              </div>
            );
          })}
        </div>
      );
    },
  },

  // ── Outcome co-occurrence network ──────────────────────────────────────────
  // Force-directed graph showing which outcome measures co-occur in the same activity.
  // Fetches outcome-measures (for the node list) and outcome-cooccurrences in parallel.
  outcomeNetwork: {
    async fetch(flags) {
      const params = { ...(flags.filterParams || {}) };
      if (flags.activityIds?.length > 0) {
        params.activityIds = flags.activityIds.join(',');
      } else if (flags.programIds?.length > 0) {
        params.programIds = flags.programIds.join(',');
      } else if (flags.orgId) {
        params.orgId = flags.orgId;
      }
      const [measures, cooc] = await Promise.all([
        chartApiGet('outcome-measures', params),
        chartApiGet('outcome-cooccurrences', params),
      ]);
      return {
        outcomes: measures.outcomes || [],
        links: cooc.links || [],
        pairedCounts: cooc.paired_counts || [],
        soloCounts: cooc.solo_counts || [],
        planCount: cooc.plan_count || 0,
        dualActivityCount: cooc.dual_activity_count || 0,
        singleActivityCount: cooc.single_activity_count || 0,
      };
    },

    render({ data, showLinkCounts, showNodeLabels }) {
      const outcomes = Array.isArray(data?.outcomes) ? data.outcomes : [];
      const links = Array.isArray(data?.links) ? data.links : [];
      const pairedCounts = Array.isArray(data?.pairedCounts)
        ? data.pairedCounts
        : [];
      const soloCounts = Array.isArray(data?.soloCounts) ? data.soloCounts : [];
      const planCount = data?.planCount || 0;
      const dualActivityCount = data?.dualActivityCount || 0;
      const singleActivityCount = data?.singleActivityCount || 0;
      return (
        <OutcomeNetwork
          outcomes={outcomes}
          links={links}
          pairedCounts={pairedCounts}
          soloCounts={soloCounts}
          planCount={planCount}
          dualActivityCount={dualActivityCount}
          singleActivityCount={singleActivityCount}
          showLinkCounts={showLinkCounts !== false}
          showNodeLabels={!!showNodeLabels}
        />
      );
    },
  },

  participantsByActivityType: {
    async fetch(flags) {
      const params = { ...(flags.filterParams || {}) };
      if (flags.activityIds?.length > 0) {
        params.activityIds = flags.activityIds.join(',');
      } else if (flags.programIds?.length > 0) {
        params.programIds = flags.programIds.join(',');
      } else if (flags.orgId) {
        params.orgId = flags.orgId;
      }
      return chartApiGet('participant-counts-by-activity-type', params);
    },

    render({ data }) {
      return <ParticipantsByActivityType data={data} />;
    },
  },

  // ── Activity type ladder ────────────────────────────────────────────────────
  // Ladder bar chart grouped by Infrastructure / Developmental / Presentational.
  participantTypeLadder: {
    async fetch(flags) {
      const params = { ...(flags.filterParams || {}) };
      if (flags.activityIds?.length > 0) {
        params.activityIds = flags.activityIds.join(',');
      } else if (flags.programIds?.length > 0) {
        params.programIds = flags.programIds.join(',');
      } else if (flags.orgId) {
        params.orgId = flags.orgId;
      }
      return chartApiGet('participant-types', params);
    },

    render({ data, hideUnused }) {
      return <ParticipantLadder data={data} hideUnused={!!hideUnused} />;
    },
  },

  activityTypeLadder: {
    async fetch(flags) {
      const params = { ...(flags.filterParams || {}) };
      if (flags.activityIds?.length > 0) {
        params.activityIds = flags.activityIds.join(',');
      } else if (flags.programIds?.length > 0) {
        params.programIds = flags.programIds.join(',');
      } else if (flags.orgId) {
        params.orgId = flags.orgId;
      }
      return chartApiGet('activity-types', params);
    },

    render({ data, title, sub, hideUnused, displayView }) {
      return (
        <ActivityTypeLadder
          data={data}
          hideUnused={!!hideUnused}
          view={displayView || 'grouped'}
        />
      );
    },
  },
  // ── Inputs: Venues ───────────────────────────────────────────────────────
  // Ranked bar chart by activity-venue link count, per venue type.
  inputsVenues: {
    async fetch(flags) {
      const params = { ...(flags.filterParams || {}) };
      if (flags.activityIds?.length > 0) {
        params.activityIds = flags.activityIds.join(',');
      } else if (flags.programIds?.length > 0) {
        params.programIds = flags.programIds.join(',');
      } else if (flags.orgId) {
        params.orgId = flags.orgId;
      }
      return chartApiGet('venues', params);
    },

    render({ data, hideUnused = true }) {
      const BAR_COLOR = '#46D200';
      const allTypes = Array.isArray(data?.types) ? data.types : [];
      const types = hideUnused
        ? allTypes.filter((t) => t.linkCount > 0)
        : allTypes;

      if (allTypes.length === 0) {
        return (
          <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>
            {t('charts.no_venu_data_for_completed_activities')}
          </div>
        );
      }

      const maxCount = Math.max(...types.map((t) => t.linkCount), 1);

      return (
        <div style={{ display: 'grid', gap: 7 }}>
          {types.map((t, idx) => {
            const count = t.linkCount;
            const pct = (count / maxCount) * MAX_BAR_PCT;
            const isTopBar = idx === 0;

            return (
              <div
                key={t.name}
                style={{ display: 'flex', alignItems: 'center', gap: 8 }}
              >
                {/* Label column — 33% */}
                <div
                  style={{
                    width: '33%',
                    flexShrink: 0,
                    fontSize: 16,
                    color: '#374151',
                    lineHeight: 1.2,
                    overflowWrap: 'break-word',
                  }}
                >
                  {t.name}
                </div>
                {/* Bar + count area */}
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      ...(isTopBar
                        ? { flex: 1 }
                        : { width: `${pct}%`, flexShrink: 0, minWidth: 6 }),
                      height: 34,
                      background: BAR_COLOR,
                      borderRadius: 4,
                    }}
                  />
                  <span
                    style={{
                      paddingLeft: 8,
                      fontSize: 16,
                      fontWeight: 600,
                      color: '#374151',
                      flexShrink: 0,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {fmtN(count)}
                  </span>
                </div>
              </div>
            );
          })}

          {/* Footer */}
          <div style={{ marginTop: 4, fontSize: 11, color: '#6b7280' }}>
            {t('charts.venues.num_unique_venues', { count: data.uniqueVenues })}
            {' · '}
            {t('charts.venues.num_venue_links', { count: data.totalLinks })}
          </div>
        </div>
      );
    },
  },

  // ── Inputs: Investment ────────────────────────────────────────────────────
  // Single horizontal segmented bar: Org grants · Partner funding · In-kind ·
  // Other expenditure · Volunteer value
  inputsInvestment: {
    async fetch(flags) {
      const params = { ...(flags.filterParams || {}) };
      if (flags.activityIds?.length > 0) {
        params.activityIds = flags.activityIds.join(',');
      } else if (flags.programIds?.length > 0) {
        params.programIds = flags.programIds.join(',');
      } else if (flags.orgId) {
        params.orgId = flags.orgId;
      }
      return chartApiGet('investment', params);
    },

    render({ data }) {
      if (!data) return null;

      const values = INVESTMENT_SEGS.map((s) => ({
        ...s,
        value: Number(data[s.key]) || 0,
      }));
      const total = values.reduce((sum, s) => sum + s.value, 0);

      if (total === 0) {
        return (
          <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>
            {t('charts.no_investment_data_for_completed_activities')}
          </div>
        );
      }

      return (
        <div style={{ display: 'grid', gap: 8 }}>
          {/* Segmented bar with hover tooltips */}
          <InvestmentChart values={values} total={total} />

          {/* Total label */}
          <div
            style={{
              textAlign: 'right',
              fontSize: 12,
              fontWeight: 600,
              color: '#374151',
            }}
          >
            {t('common.total')}: {fmtCurrencyCompact(total)}
            {data.volunteerCount > 0 && (
              <span
                style={{
                  fontWeight: 400,
                  color: '#6b7280',
                  marginLeft: 6,
                }}
              >
                (
                {t('charts.including_volunteers', {
                  volunteers: t('charts.num_volunteers', {
                    count: data.volunteerCount,
                    countFmt: fmtN(data.volunteerCount),
                  }),
                })}
                )
              </span>
            )}
          </div>

          {/* Legend */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px 20px',
              marginTop: 4,
            }}
          >
            {values
              .filter((s) => s.value > 0)
              .map((s, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 7,
                    fontSize: 11,
                  }}
                >
                  <div
                    style={{
                      width: 28,
                      height: 16,
                      borderRadius: 3,
                      background: s.color,
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ color: '#374151' }}>{s.label}</span>
                  <span style={{ color: '#6b7280', fontWeight: 600 }}>
                    {fmtCurrencyCompact(s.value)}
                  </span>
                  {s.key === 'volunteerValue' && data.volunteerCount > 0 && (
                    <span
                      style={{
                        fontWeight: 400,
                        color: '#9ca3af',
                        fontSize: 11,
                        marginLeft: 2,
                      }}
                    >
                      (
                      {t('charts.estimated_hours_per_volunteer', {
                        hours: t('charts.num_hours', {
                          count: data.volunteerHoursPerSession ?? 3,
                          countFmt: fmtN(data.volunteerHoursPerSession ?? 3),
                        }),
                      })}{' '}
                      ×{fmtCurrency(data.volunteerHourlyRate ?? 52.19)}
                      {t('charts.per_hour_compact')})
                    </span>
                  )}
                </div>
              ))}
          </div>

          {/* Completed-only note */}
          {data.hasNonCompleted && (
            <div
              style={{
                fontSize: 11,
                color: '#6b7280',
                fontStyle: 'italic',
                marginTop: 2,
              }}
            >
              {t('charts.only_completed_activities_included')}
            </div>
          )}
        </div>
      );
    },
  },

  // ── Inputs: Partners ──────────────────────────────────────────────────────
  // Three-row horizontal bar chart: FUNDING / IN_KIND / NETWORK (all activities).
  // NETWORK has no dollar value — shows partner count only.
  inputsPartners: {
    async fetch(flags) {
      const params = { ...(flags.filterParams || {}) };
      if (flags.activityIds?.length > 0) {
        params.activityIds = flags.activityIds.join(',');
      } else if (flags.programIds?.length > 0) {
        params.programIds = flags.programIds.join(',');
      } else if (flags.orgId) {
        params.orgId = flags.orgId;
      }
      return chartApiGet('partners', params);
    },

    render({ data, hideUnused = true }) {
      const BAR_COLOR = '#0C95CD';
      const allCategories = Array.isArray(data?.categories)
        ? data.categories
        : [];
      const categories = hideUnused
        ? allCategories.filter((c) => c.count > 0)
        : allCategories;

      if (
        allCategories.length === 0 ||
        allCategories.every((c) => c.count === 0)
      ) {
        return (
          <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>
            {t('charts.no_partner_data_for_completed_activities')}
          </div>
        );
      }

      const fmtC = (n) => {
        const v = Math.round(Number(n) || 0);
        if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}m`;
        if (v >= 10_000) return `$${Math.round(v / 1_000)}k`;
        if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}k`;
        return `$${v.toLocaleString()}`;
      };

      // Bar width based on dollar value; top bar uses flex:1
      const maxValue = Math.max(...categories.map((c) => c.total), 1);

      return (
        <div style={{ display: 'grid', gap: 7 }}>
          {categories.map((cat) => {
            const hasValue = cat.total > 0;
            const pct = hasValue ? (cat.total / maxValue) * MAX_BAR_PCT : 0;
            const isTopBar = hasValue && cat.total === maxValue;

            return (
              <div
                key={cat.type}
                style={{ display: 'flex', alignItems: 'center', gap: 8 }}
              >
                {/* Label column — 33% */}
                <div
                  style={{
                    width: '33%',
                    flexShrink: 0,
                    fontSize: 16,
                    color: cat.count > 0 ? '#374151' : '#9ca3af',
                    lineHeight: 1.2,
                    overflowWrap: 'break-word',
                  }}
                >
                  {cat.label}
                </div>

                {/* Bar + value/count area */}
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    minWidth: 0,
                  }}
                >
                  {cat.count === 0 ? (
                    <span
                      style={{
                        fontSize: 16,
                        color: '#9ca3af',
                        fontStyle: 'italic',
                      }}
                    >
                      {t('charts.none_recorded')}
                    </span>
                  ) : hasValue ? (
                    <>
                      <div
                        style={{
                          ...(isTopBar
                            ? { flex: 1 }
                            : {
                                width: `${pct}%`,
                                flexShrink: 0,
                                minWidth: 6,
                              }),
                          height: 34,
                          background: BAR_COLOR,
                          borderRadius: 4,
                          boxSizing: 'border-box',
                        }}
                      />
                      <span
                        style={{
                          paddingLeft: 8,
                          fontSize: 16,
                          fontWeight: 600,
                          color: '#374151',
                          flexShrink: 0,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {fmtC(cat.total)}
                      </span>
                      <span
                        style={{
                          marginLeft: 8,
                          fontSize: 16,
                          color: '#6b7280',
                          flexShrink: 0,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {t('common.partner_with_cnt', {
                          count: cat.count,
                          countFmt: fmtN(cat.count),
                        })}
                      </span>
                    </>
                  ) : (
                    <span style={{ fontSize: 16, color: '#374151' }}>
                      {t('common.partner_with_cnt', {
                        count: cat.count,
                        countFmt: fmtN(cat.count),
                      })}{' '}
                      ({t('charts.no_dollar_value')})
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      );
    },
  },
};

export default ChartRegistry;
